import React, { useState } from 'react';
import { Database } from '../types/supabase';
import { Trash2, Edit2, Calendar, FileText, Eye } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type Task = Database['public']['Tables']['tasks']['Row'];

interface KanbanCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onViewTask: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onToggleComplete: (taskId: string, completed: boolean) => void;
  onUpdateStatus?: (taskId: string, status: string) => void;
}

export function KanbanCard({
  task,
  onEdit,
  onViewTask,
  onDelete,
  onToggleComplete,
  onUpdateStatus,
}: KanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Not Started':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'Testing':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200';
      case 'Awaiting Feedback':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200';
      case 'Completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const statusOptions = [
    'Not Started',
    'In Progress',
    'Testing',
    'Awaiting Feedback',
    'Completed'
  ];

  const handleStatusChange = async (newStatus: string) => {
    if (onUpdateStatus) {
      await onUpdateStatus(task.id, newStatus);
      onToggleComplete(task.id, newStatus === 'Completed');
    }
    setShowStatusMenu(false);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-all duration-200 p-4 cursor-move ${
        task.completed ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="min-w-0 flex-1">
            <h4
              className={`text-sm font-medium line-clamp-2 ${
                task.completed
                  ? 'line-through text-gray-500 dark:text-gray-400'
                  : 'text-gray-900 dark:text-white'
              }`}
            >
              {task.title}
            </h4>
            <div className="mt-2 relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowStatusMenu(!showStatusMenu);
                }}
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                  task.status
                )}`}
              >
                {task.status}
              </button>
              {showStatusMenu && (
                <div className="absolute left-0 mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1">
                    {statusOptions.map((status) => (
                      <button
                        key={status}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleStatusChange(status);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          task.status === status
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {task.due_date && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {new Date(task.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onViewTask(task);
            }}
            className="text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors"
          >
            <Eye className="h-4 w-4" aria-label="View task details" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete(task.id);
            }}
            className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            <Trash2 className="h-4 w-4" aria-label="Delete task" />
          </button>
        </div>
      </div>
    </div>
  );
}